import { HttpClient, httpResource, HttpResourceRef } from "@angular/common/http";
import { inject, Injectable, Signal } from "@angular/core";
import { Observable, of, combineLatest, firstValueFrom } from "rxjs";
import { map, shareReplay, catchError } from "rxjs/operators";
import { IntegrationCapabilitiesResource } from "../models/integration-capabilities-resource.model";
import { IntegrationCapability } from "../models/integration-capability.model";
import { IntegrationCapabilityDisplayName } from "../models/integration-capability-display-name.model";
import { IntegrationVisit } from "../models/integration-visit.model";
import { FacilityResource } from "../models/facility-resource.model";
import { IntegrationReviewer } from "../models/integration-reviewer.model";
import { IntegrationReviewerGroup } from "../models/integration-reviewer-group.model";
import { IntegrationPatient } from "../models/integration-patient.model";
import { IntegrationOrder } from "../models/integration-order.model";
import { ReviewerQuery } from "../models/reviewer-query.model";
import { OrderQuery } from "../models/order-query.model";
import { AddIntegrationPatient } from "../models/add-integration-patient.model";
import { PatientQuery } from "../models/patient-query.model";
import { SettingsService } from "../settings/settings.service";

@Injectable({ providedIn: "root" })
export class IntegrationsService {
    private httpClient = inject(HttpClient);
    private settingsService = inject(SettingsService);

    private capabilities$ = this.httpClient.get<IntegrationCapabilitiesResource>("/api/integrations/capabilities").pipe(shareReplay(1));
    public getCapabilities$(): Observable<IntegrationCapabilitiesResource> {
        return this.capabilities$;
    }

    public getCapability$(displayName: IntegrationCapabilityDisplayName): Observable<IntegrationCapability | undefined> {
        return this.capabilities$.pipe(map((res) => res?.capabilities?.find((capability) => capability.display === displayName)));
    }

    public hasCapability$(displayName: IntegrationCapabilityDisplayName): Observable<boolean> {
        return this.capabilities$.pipe(
            map((res) => res?.capabilities?.some((capability) => capability.display === displayName) || false),
            catchError(() => of(false))
        );
    }

    public getCapabilityPropertyNames$(displayName: IntegrationCapabilityDisplayName): Observable<Array<string>> {
        return this.capabilities$.pipe(map((res) => res?.capabilities?.find((c) => c.display === displayName)?.properties?.map((p) => p.name) || []));
    }

    public getCustomPatientLookupTemplate$(): Observable<string | undefined> {
        return this.capabilities$.pipe(map((res) => res?.customPatientLookupTemplate));
    }

    public searchPatientVisits(query: Signal<{ patientId: string }>): HttpResourceRef<Array<IntegrationVisit>> {
        return httpResource(() => {
            const payload = query();
            if (!payload || !payload.patientId) return;
            return {
                url: "/api/integrations/visits",
                method: "POST",
                body: payload
            };
        });
    }

    public getVisits$(patientId: string): Observable<Array<IntegrationVisit>> {
        return this.httpClient.post<Array<IntegrationVisit>>("/api/integrations/visits", { patientId });
    }

    public getIntegrationFacilities$(): Observable<Array<FacilityResource>> {
        return this.httpClient.get<Array<FacilityResource>>("/api/integrations/facilities");
    }

    public getReviewers$(query: ReviewerQuery): Observable<Array<IntegrationReviewer>> {
        return this.httpClient.post<Array<IntegrationReviewer>>("/api/integrations/reviewers", query);
    }

    public getReviewerGroups$(): Observable<Array<IntegrationReviewerGroup>> {
        return this.httpClient.get<Array<IntegrationReviewerGroup>>("/api/integrations/reviewergroups");
    }

    public searchPatients(query: Signal<PatientQuery>): HttpResourceRef<Array<IntegrationPatient>> {
        return httpResource(() => {
            const payload = query();
            if (!payload || !Object.keys(payload).length) return undefined;
            return {
                url: "/api/integrations/patients",
                method: "POST",
                body: payload
            };
        });
    }

    public searchPatients$(query: PatientQuery): Observable<Array<IntegrationPatient>> {
        return this.httpClient.post<Array<IntegrationPatient>>("/api/integrations/patients", query);
    }

    public getOrders$(query: OrderQuery): Observable<Array<IntegrationOrder>> {
        return this.httpClient.post<Array<IntegrationOrder>>("/api/integrations/orders", query);
    }

    public hideVisitLookup$(): Observable<boolean> {
        return combineLatest([this.hasCapability$("visitLookup"), this.settingsService.areVisitDetailsReadOnly$()]).pipe(
            map(([lookupEnabled, visitsReadonly]) => !lookupEnabled || visitsReadonly)
        );
    }

    public addPatient(patient: AddIntegrationPatient): Promise<IntegrationPatient> {
        return firstValueFrom(this.httpClient.post<IntegrationPatient>("/api/integrations/patients/add", patient));
    }

    public clearCachedData(): Promise<any> {
        return firstValueFrom(this.httpClient.post<any>("/api/integrations/clearcache", {}));
    }
}
